import {createWiredGroup, cube} from "../creators";
import {createText} from "../createText";
import {createLoopScaleTween, createMoveToTween} from "../tweens";
import * as THREE from "three";
import {Mesh, Vector3} from "three";
import {LineGeometry} from "three/examples/jsm/lines/LineGeometry";
import {LineMaterial} from "three/examples/jsm/lines/LineMaterial";
import utility from "../utility";
import {Font, FontLoader} from "three/examples/jsm/loaders/FontLoader";
import Logger from "../../Logger";
import {Message} from "../../stores/MessageStore";
import {Material} from "three/src/materials/Material";

const easingArray = utility.easingArray;

const logger = new Logger("WiredEffect");
type WiredGroup = THREE.Group & {
    textMesh?: Mesh
};

class WiredChatEffect {
    scene: THREE.Scene;
    wiredGroups: WiredGroup[];
    startPosition: Vector3;
    cubeSize: number;
    geometry: LineGeometry;
    materials: THREE.MeshPhongMaterial[];
    material: LineMaterial;
    private font?: Font;

    constructor(scene: THREE.Scene) {
        this.scene = scene;
        this.wiredGroups = [];

        const startPosition = [-425, -512, -955];
        const [x, y, z] = startPosition;

        this.startPosition = new Vector3(x, y, z);

        const cubeSize = 100
        this.cubeSize = cubeSize;
        this.geometry = new LineGeometry();
        this.geometry.setPositions(cube(cubeSize));

        this.materials = [
            new THREE.MeshPhongMaterial({color: 0xffffff, flatShading: true}), // front
            new THREE.MeshPhongMaterial({color: 0xffffff}) // side
        ];
        // Line style
        // NOTE 2: Instantiate a LineMaterial instead of LineBasicMaterial
        // with a resolution parameter.
        this.material = new LineMaterial({
            color: 0xffffff,
            linewidth: 1, // Does nothing due to OpenGL limit
            resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
            // fog: true,
        });

    }

    async init() {
        await this.loadFonts();
    }

    run(message: Message) {
        if (!message.content) {
            return;
        }

        this.addWiredGroup(message.content);
    }

    loadFonts() {
        const loader = new FontLoader();
        logger.trace("loading fonts");

        return new Promise<void>((resolve, reject) => {
            loader.load('/fonts/helvetiker_regular.typeface.json', font => {
                this.font = font;
                resolve();
            }, undefined, error => reject(error));
        })
    }

    addWiredGroup(text: string) {
        const duration = 1000 + Math.random() * 9000

        const wiredGroup: WiredGroup = createWiredGroup(this.geometry, this.material, this.cubeSize);

        if (text && this.font) {
            const textMesh: Mesh = createText({
                text,
                font: this.font,
                materials: this.materials
            });

            wiredGroup.add(textMesh);
            wiredGroup.textMesh = textMesh;
        }

        wiredGroup.position.copy(this.startPosition)
        this.wiredGroups.push(wiredGroup);
        this.scene.add(wiredGroup)

        const tween = createMoveToTween(this.scene, wiredGroup, new Vector3(0, 0, -10600), {
            duration,
        })
        const easingObject = easingArray[Math.floor(Math.random() * easingArray.length)];
        const scaleTween = createLoopScaleTween(wiredGroup, {
            duration: Math.random() * 1000,
            easing: easingObject.easing,
        })

        scaleTween.start();

        tween.start().onComplete(() => {
            if (wiredGroup.textMesh) {
                // const maxLength = 10;
                // let text =   wiredGroup.textMesh.textString.slice(0, maxLength);
                // if(wiredGroup.textMesh.textString.length > maxLength){
                //   text += "...";
                // }
                // logger.info("disposing wired group textMesh. text:" + text)
                if (wiredGroup.textMesh.geometry) {
                    // logger.info("disposing wired group textMesh geometry");
                    wiredGroup.textMesh.geometry.dispose();
                }

                const material = wiredGroup.textMesh.material;
                if ((material as Material[]).length > 1) {
                    const materials = material as Material[]
                    // logger.info("disposing wired group textMesh material");
                    materials[0].dispose();
                    materials[1].dispose();
                }
            }
            this.wiredGroups = this.wiredGroups.filter(g => g !== wiredGroup);
            this.scene.remove(wiredGroup);
            scaleTween.stop();
        })
    }

}

export default WiredChatEffect;
